
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Space+Mono:wght@400;700&display=swap');

  :root {
    --bg: #050a0f;
    --surface: #0c1419;
    --surface2: #111c24;
    --accent: #00d4ff;
    --accent2: #ff6b35;
    --gold: #e8c97a;
    --green: #00ff88;
    --violet: #a855f7;
    --text: #d8e8f0;
    --muted: #5a7a8a;
    --border: rgba(0,212,255,0.12);
  }

  .ak-root {
    background: var(--bg);
    color: var(--text);
    font-family: 'Space Mono', monospace;
    font-size: 13px;
    overflow-x: hidden;
    line-height: 1.6;
    position: relative;
  }

  .ak-root * { margin:0; padding:0; box-sizing:border-box; }

  .ak-grid-bg {
    position: fixed; inset: 0; pointer-events: none; z-index: 0;
    background-image:
      linear-gradient(rgba(0,212,255,0.02) 1px,transparent 1px),
      linear-gradient(90deg,rgba(0,212,255,0.02) 1px,transparent 1px);
    background-size: 64px 64px;
  }

  .ak-glow {
    position: fixed;
    top: -200px; left: 50%;
    transform: translateX(-50%);
    width: 900px; height: 400px;
    background: radial-gradient(ellipse, rgba(0,212,255,0.06) 0%, transparent 70%);
    pointer-events: none; z-index: 0;
  }

  .ak-container { max-width: 1120px; margin: 0 auto; padding: 0 2.5rem; position: relative; z-index: 1; }

  @keyframes ak-fadeUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
  @keyframes ak-blink  { 0%,100%{opacity:1} 50%{opacity:0} }
  @keyframes ak-pulseDot { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(1.5);opacity:0.6} }
  @keyframes ak-scanH { 0%{transform:translateX(-100%)} 100%{transform:translateX(100vw)} }

  .ak-cursor { display:inline-block; width:2px; height:.9em; background:var(--accent); vertical-align:middle; margin-left:2px; animation:ak-blink 1s step-end infinite; }
  .ak-dot-live { display:inline-block; width:8px; height:8px; border-radius:50%; background:var(--green); box-shadow:0 0 12px var(--green); animation:ak-pulseDot 2.2s ease-in-out infinite; flex-shrink:0; }

  /* HERO */
  .ak-hero {
    min-height: 100vh;
    display: grid;
    grid-template-columns: 1.2fr 0.8fr;
    align-items: center;
    gap: 5rem;
    padding: 8rem 2.5rem 5rem;
    max-width: 1120px;
    margin: 0 auto;
    position: relative; z-index: 1;
  }

  .ak-hero-eyebrow { display:flex; align-items:center; gap:.8rem; margin-bottom:2rem; font-size:9px; letter-spacing:.32em; text-transform:uppercase; color:var(--muted); }

  .ak-hero h1 { font-family:'Cormorant Garamond',serif; font-size:clamp(4rem,6.5vw,6.2rem); font-weight:300; line-height:1.0; color:#fff; animation:ak-fadeUp .9s ease both; }
  .ak-hero h1 em { font-style:italic; color:var(--gold); }

  .ak-hero-role { font-size:9px; letter-spacing:.24em; text-transform:uppercase; color:var(--accent); margin:1.8rem 0 1.8rem; animation:ak-fadeUp .9s .12s ease both; }

  .ak-hero-bio { font-family:'Cormorant Garamond',serif; font-size:1.22rem; font-weight:300; line-height:1.85; color:#6a8a9a; animation:ak-fadeUp .9s .25s ease both; margin-bottom:2.2rem; }

  .ak-hero-tags { display:flex; flex-wrap:wrap; gap:.4rem; animation:ak-fadeUp .9s .4s ease both; }

  .ak-tag { font-size:8px; letter-spacing:.12em; text-transform:uppercase; padding:.35rem .75rem; border:1px solid var(--border); color:var(--accent); transition:all .25s; cursor:default; }
  .ak-tag:hover { background:rgba(0,212,255,.07); border-color:rgba(0,212,255,.4); }
  .ak-tag.gold { color:var(--gold); border-color:rgba(232,201,122,.25); }
  .ak-tag.gold:hover { background:rgba(232,201,122,.07); }

  /* STATS */
  .ak-stats-panel { display:grid; grid-template-columns:1fr 1fr; gap:1px; background:var(--border); border:1px solid var(--border); animation:ak-fadeUp .9s .18s ease both; }
  .ak-stat-cell { background:var(--surface); padding:1.75rem 1.4rem; transition:background .3s; position:relative; overflow:hidden; }
  .ak-stat-cell::after { content:''; position:absolute; bottom:0; left:0; width:0; height:1px; background:var(--accent); transition:width .4s; }
  .ak-stat-cell:hover::after { width:100%; }
  .ak-stat-cell:hover { background:var(--surface2); }
  .ak-stat-num { font-family:'Cormorant Garamond',serif; font-size:2.4rem; font-weight:300; color:var(--gold); line-height:1; display:block; }
  .ak-stat-label { font-size:8px; letter-spacing:.16em; text-transform:uppercase; color:var(--muted); margin-top:.35rem; display:block; }

  /* DIVIDER */
  .ak-divider { width:100%; height:1px; position:relative; z-index:1; overflow:hidden; background:linear-gradient(90deg,transparent,rgba(0,212,255,.15),rgba(232,201,122,.15),transparent); }
  .ak-divider::after { content:''; position:absolute; top:0; left:0; width:120px; height:100%; background:linear-gradient(90deg,transparent,var(--accent),transparent); animation:ak-scanH 5s linear infinite; }

  /* SECTIONS */
  .ak-section { padding:5rem 0; position:relative; z-index:1; }
  .ak-section.dark { background:var(--surface); }

  .ak-section-header { display:flex; align-items:center; gap:1.5rem; margin-bottom:3rem; }
  .ak-section-num { font-size:8px; letter-spacing:.3em; color:var(--accent); text-transform:uppercase; flex-shrink:0; }
  .ak-section-title { font-family:'Cormorant Garamond',serif; font-size:2.5rem; font-weight:300; color:#fff; }
  .ak-section-line { height:1px; background:var(--border); flex:1; }
  .ak-section-sub { font-family:'Cormorant Garamond',serif; font-size:1.1rem; color:var(--muted); max-width:680px; margin-bottom:3rem; margin-top:-1.5rem; line-height:1.7; }

  /* IMPACT GRID */
  .ak-impact-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:1px; background:var(--border); }
  .ak-impact-card { background:var(--surface2); padding:2.2rem 1.8rem; position:relative; overflow:hidden; transition:background .3s; }
  .ak-impact-card::before { content:''; position:absolute; top:0; left:0; right:0; height:2px; background:linear-gradient(90deg,var(--gold),transparent); transform:scaleX(0); transform-origin:left; transition:transform .45s; }
  .ak-impact-card:hover::before { transform:scaleX(1); }
  .ak-impact-card:hover { background:#141f27; }
  .ak-impact-num { font-family:'Cormorant Garamond',serif; font-size:2.7rem; font-weight:300; color:var(--gold); line-height:1; display:block; margin-bottom:.4rem; }
  .ak-impact-label { font-size:8px; letter-spacing:.14em; text-transform:uppercase; color:var(--accent); margin-bottom:.5rem; display:block; }
  .ak-impact-desc { font-family:'Cormorant Garamond',serif; font-size:.95rem; color:var(--muted); line-height:1.65; }

  /* PILLARS */
  .ak-pillars-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:1px; background:var(--border); }
  .ak-pillar-card { background:var(--surface); padding:2rem 1.5rem; position:relative; overflow:hidden; transition:background .3s; cursor:default; }
  .ak-pillar-card:hover { background:var(--surface2); }
  .ak-pillar-card::after { content:''; position:absolute; bottom:0; left:0; right:0; height:2px; background:var(--accent); transform:scaleX(0); transform-origin:left; transition:transform .4s; }
  .ak-pillar-card:hover::after { transform:scaleX(1); }
  .ak-pillar-icon { font-size:1.6rem; display:block; margin-bottom:1rem; }
  .ak-pillar-title { font-family:'Cormorant Garamond',serif; font-size:1.2rem; color:#fff; margin-bottom:.5rem; }
  .ak-pillar-body { font-family:'Cormorant Garamond',serif; font-size:.95rem; color:var(--muted); line-height:1.65; }
  .ak-pillar-chips { display:flex; flex-wrap:wrap; gap:.3rem; margin-top:.85rem; }

  /* SOLUTIONS */
  .ak-solutions-grid { display:grid; grid-template-columns:1fr 1fr; gap:1px; background:var(--border); }
  .ak-solution-card { background:var(--surface); padding:2.4rem; position:relative; overflow:hidden; transition:background .3s; }
  .ak-solution-card:hover { background:var(--surface2); }
  .ak-solution-card::after { content:''; position:absolute; top:0; right:0; bottom:0; width:2px; background:linear-gradient(180deg,transparent,var(--accent),transparent); opacity:0; transition:opacity .3s; }
  .ak-solution-card:hover::after { opacity:1; }
  .ak-sol-client { font-size:8px; letter-spacing:.24em; text-transform:uppercase; color:var(--accent2); margin-bottom:.5rem; display:block; }
  .ak-sol-title { font-family:'Cormorant Garamond',serif; font-size:1.35rem; font-weight:400; color:#fff; margin-bottom:.8rem; line-height:1.3; }
  .ak-sol-impact { font-family:'Cormorant Garamond',serif; font-size:1rem; font-style:italic; color:var(--gold); margin-bottom:.85rem; padding-left:.75rem; border-left:2px solid rgba(232,201,122,.4); line-height:1.55; }
  .ak-sol-desc { font-family:'Cormorant Garamond',serif; font-size:1.05rem; color:#507080; line-height:1.78; }
  .ak-sol-tech { display:flex; flex-wrap:wrap; gap:.3rem; margin-top:1rem; }

  /* TECH CHIP */
  .ak-tech-chip { font-size:7px; letter-spacing:.1em; text-transform:uppercase; padding:.22rem .55rem; background:rgba(0,212,255,.05); border:1px solid rgba(0,212,255,.12); color:var(--muted); }
  .ak-tech-chip.violet { background:rgba(168,85,247,.06); border-color:rgba(168,85,247,.2); color:#a855f7; }
  .ak-tech-chip.gold { background:rgba(232,201,122,.05); border-color:rgba(232,201,122,.2); color:#c8a95a; }

  /* FEATURE CARD */
  .ak-feature-card { background:var(--surface); border:1px solid var(--border); padding:3rem; position:relative; overflow:hidden; margin-bottom:1px; }
  .ak-feature-card::before { content:''; position:absolute; inset:0; background:radial-gradient(ellipse at top right,rgba(0,212,255,.055),transparent 55%); pointer-events:none; }
  .ak-feature-badge { font-size:8px; letter-spacing:.3em; text-transform:uppercase; color:var(--accent2); border:1px solid rgba(255,107,53,.3); padding:.3rem .75rem; display:inline-block; margin-bottom:1.5rem; }
  .ak-feature-title { font-family:'Cormorant Garamond',serif; font-size:2.2rem; font-weight:300; color:#fff; margin-bottom:1rem; }
  .ak-feature-body { font-family:'Cormorant Garamond',serif; font-size:1.12rem; color:#507080; line-height:1.88; max-width:840px; margin-bottom:2rem; }
  .ak-feature-metrics { display:grid; grid-template-columns:repeat(4,1fr); gap:1px; background:var(--border); }
  .ak-fm-cell { background:rgba(0,212,255,.04); padding:1.2rem 1rem; text-align:center; }
  .ak-fm-num { font-family:'Cormorant Garamond',serif; font-size:1.5rem; color:var(--gold); display:block; }
  .ak-fm-label { font-size:7.5px; letter-spacing:.14em; text-transform:uppercase; color:var(--muted); margin-top:.2rem; }

  /* CLIENTS */
  .ak-clients-grid { display:grid; grid-template-columns:repeat(6,1fr); gap:1px; background:var(--border); }
  .ak-client-cell { background:var(--surface); min-height:80px; display:flex; align-items:center; justify-content:center; padding:1.5rem 1rem; transition:background .3s; cursor:default; }
  .ak-client-cell:hover { background:var(--surface2); }
  .ak-client-name { font-family:'Space Mono',monospace; font-size:9px; letter-spacing:.12em; text-transform:uppercase; color:var(--muted); text-align:center; line-height:1.5; transition:color .3s; }
  .ak-client-cell:hover .ak-client-name { color:#b0ccd8; }
  .ak-logo-jsw { font-size:16px; font-weight:700; letter-spacing:.1em; color:#e8c97a; border:1.5px solid rgba(232,201,122,.4); padding:.2rem .5rem; }
  .ak-logo-samsung { font-size:11px; font-weight:700; letter-spacing:.08em; color:#00d4ff; }
  .ak-logo-lg { font-size:14px; font-weight:700; color:#00d4ff; border:1.5px solid rgba(0,212,255,.35); border-radius:50%; width:34px; height:34px; display:flex; align-items:center; justify-content:center; }
  .ak-clients-note { font-size:8.5px; letter-spacing:.18em; text-transform:uppercase; color:var(--muted); margin-top:1.5rem; text-align:right; }

  /* QUOTE */
  .ak-quote-wrap { padding:5rem 0; position:relative; z-index:1; }
  .ak-quote-block { border-left:2px solid var(--gold); padding:2.5rem 3.5rem; background:var(--surface); position:relative; }
  .ak-quote-block::before { content:'"'; font-family:'Cormorant Garamond',serif; font-size:9rem; color:rgba(232,201,122,.07); position:absolute; top:-1.5rem; left:1.5rem; line-height:1; pointer-events:none; }
  .ak-quote-text { font-family:'Cormorant Garamond',serif; font-size:1.7rem; font-weight:300; font-style:italic; color:#c0d8e8; line-height:1.65; margin-bottom:1.2rem; position:relative; }
  .ak-quote-attr { font-size:9px; letter-spacing:.25em; text-transform:uppercase; color:var(--muted); }

  /* SECTORS */
  .ak-sectors-list { display:flex; flex-wrap:wrap; gap:1px; background:var(--border); }
  .ak-sector { background:var(--surface); padding:.85rem 1.4rem; font-size:8.5px; letter-spacing:.12em; text-transform:uppercase; color:var(--muted); transition:all .25s; cursor:default; }
  .ak-sector:hover { background:var(--surface2); color:var(--accent); }

  /* FOOTER */
  .ak-footer { border-top:1px solid var(--border); padding:3rem 0; position:relative; z-index:1; }
  .ak-footer-inner { display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:1.5rem; }
  .ak-footer-name { font-family:'Cormorant Garamond',serif; font-size:1.6rem; color:#fff; font-weight:300; }
  .ak-footer-meta { font-size:8.5px; letter-spacing:.14em; color:var(--muted); text-transform:uppercase; margin-top:.3rem; }
  .ak-footer-links { display:flex; flex-direction:column; align-items:flex-end; gap:.4rem; }
  .ak-footer-links a { font-size:9px; letter-spacing:.18em; text-transform:uppercase; color:var(--muted); text-decoration:none; transition:color .25s; }
  .ak-footer-links a:hover { color:var(--accent); }

  @media(max-width:900px) {
    .ak-hero { grid-template-columns:1fr; padding:5rem 1.5rem 3rem; gap:3rem; }
    .ak-stats-panel { grid-template-columns:1fr 1fr; }
    .ak-impact-grid { grid-template-columns:1fr 1fr; }
    .ak-pillars-grid { grid-template-columns:1fr 1fr; }
    .ak-solutions-grid { grid-template-columns:1fr; }
    .ak-feature-metrics { grid-template-columns:1fr 1fr; }
    .ak-clients-grid { grid-template-columns:repeat(3,1fr); }
    .ak-footer-links { align-items:flex-start; }
  }
  @media(max-width:580px) {
    .ak-impact-grid { grid-template-columns:1fr; }
    .ak-pillars-grid { grid-template-columns:1fr; }
    .ak-clients-grid { grid-template-columns:repeat(2,1fr); }
  }
`;

const pillars = [
  {
    icon: "◈",
    title: "Artificial Intelligence & ML",
    body: "Predictive analytics, intelligent automation, NLP, and data science pipelines deployed across manufacturing, healthcare, and education at enterprise scale.",
    chips: [
      { label: "Predictive ML", v: "violet" },
      { label: "NLP", v: "violet" },
      { label: "Data Pipelines", v: "violet" },
    ],
  },
  {
    icon: "◉",
    title: "Vision AI",
    body: "Computer vision systems for defect detection, object recognition, quality inspection, people counting, and surveillance intelligence — deployed on edge hardware and cloud-connected platforms.",
    chips: [
      { label: "Object Detection", v: "gold" },
      { label: "Defect Inspection", v: "gold" },
      { label: "Edge AI", v: "gold" },
    ],
  },
  {
    icon: "⬡",
    title: "IIoT & IoT Platforms",
    body: "Industrial Internet of Things ecosystems connecting machines, sensors, RFID readers, and barcode systems into unified real-time intelligence platforms with SAP, cloud, and mobile integration.",
    chips: [
      { label: "RFID" },
      { label: "Sensors" },
      { label: "Real-time Sync" },
      { label: "SAP" },
    ],
  },
  {
    icon: "▲",
    title: "Drone Technology Software",
    body: "Custom software platforms for drone fleet management, mission planning, aerial data processing, inspection automation, and real-time telemetry dashboards for industrial and defence applications.",
    chips: [
      { label: "Fleet Management", v: "gold" },
      { label: "Telemetry", v: "gold" },
      { label: "Inspection AI", v: "gold" },
    ],
  },
  {
    icon: "◆",
    title: "Mobile & Full-Stack Web",
    body: "Android, iOS, Flutter, and React Native applications — from consumer-scale security apps used by millions to industrial field tools running on warehouse floor tablets.",
    chips: [
      { label: "Flutter" },
      { label: "React Native" },
      { label: "Node.js" },
    ],
  },
  {
    icon: "◐",
    title: "Industrial Automation & AR/VR",
    body: "Smart factory platforms, QR/RFID-driven process automation, ERP integration, and augmented reality training applications — designed for the floor, not the boardroom.",
    chips: [
      { label: "AR/VR" },
      { label: "ERP / SAP" },
      { label: "Smart Factory" },
    ],
  },
  {
    icon: "▣",
    title: "Cyber Security",
    body: "256-bit encrypted platforms, role-based access control, government-grade security applications, and digital infrastructure protection — including the BrowseSafe app for all Indian citizens.",
    chips: [
      { label: "Encryption" },
      { label: "RBAC" },
      { label: "Govt-grade Security" },
    ],
  },
  {
    icon: "◎",
    title: "Cloud & Data Intelligence",
    body: "AutoScaling cloud architectures, Power BI-style dashboards, real-time analytics engines, and data warehouse solutions — turning raw industrial data into boardroom-ready decisions.",
    chips: [
      { label: "AutoScale DB" },
      { label: "Dashboards" },
      { label: "Analytics" },
    ],
  },
];

const impactMetrics = [
  {
    num: "₹80,000 Cr+",
    label: "Machinery Under Management",
    desc: "IIoT-connected asset management platforms track, maintain, and optimise over ₹80,000 Crore worth of industrial machinery — giving enterprises real-time visibility into every asset.",
  },
  {
    num: "₹12,000 Cr+",
    label: "Inventory Under Management",
    desc: "Intelligent RFID and QR-based inventory platforms manage assets worth over ₹12,000 Crore for industrial giants, with end-to-end real-time traceability and audit-ready reporting.",
  },
  {
    num: "16,800/hr",
    label: "Paint Barrels Tracked — JSW",
    desc: "A custom RFID warehouse automation system for JSW Paints processes 16,800 paint barrels every hour — 40% faster than leading global industrial systems.",
  },
  {
    num: "90%",
    label: "Reduction in Dispatch Errors",
    desc: "AI-powered tracking and automation eliminated near-all dispatch errors across enterprise warehouses, replacing manual processes with intelligent, self-correcting systems.",
  },
  {
    num: "60%",
    label: "Labour Reduction in Inventory Ops",
    desc: "Smart automation platforms have freed enterprise workforces from manual, error-prone inventory operations — redirecting human effort to higher-value activities.",
  },
  {
    num: "₹20,000 Cr+",
    label: "Vendor Material Managed Daily",
    desc: "RFID and QR-powered vendor material management systems sync in real time with SAP, delivering full traceability across complex, multi-supplier industrial supply chains every single day.",
  },
];

const solutions = [
  {
    client: "JSW Paints · IIoT + RFID Warehouse",
    title: "Smart RFID Inventory Automation System",
    impact:
      "16,800 paint barrels tracked per hour — 40% faster than leading global industrial platforms.",
    desc: "JSW's paint warehouses ran on manual tracking: invisible, error-prone, and impossible to scale. Ajinkya deployed a fully custom IIoT-connected RFID system — integrating forklift operations, structured binning, FIFO management, and role-based access into one unified real-time dashboard. An Android scanner app extended intelligence to the warehouse floor. The result: near-zero dispatch errors and a throughput no off-the-shelf solution could match.",
    chips: [
      { label: "RFID" },
      { label: "IIoT" },
      { label: "ReactJS" },
      { label: "PHP CI" },
      { label: "MySQL AutoScale" },
      { label: "Android" },
    ],
  },
  {
    client: "Vision AI · Industrial Quality Control",
    title: "Vision AI — Automated Defect Detection & Quality Inspection",
    impact:
      "Camera-based intelligence replacing human inspectors — catching defects in milliseconds, at scale.",
    desc: "Manual quality inspection is slow, inconsistent, and expensive. Ajinkya's Vision AI systems deploy camera networks with real-time computer vision models to detect surface defects, dimensional anomalies, misaligned components, and foreign objects on production lines. The system generates instant alerts, logs every rejection with visual evidence, and feeds a live analytics dashboard — delivering consistent, auditable quality intelligence that no human-led inspection line can match.",
    chips: [
      { label: "Vision AI", v: "violet" },
      { label: "Computer Vision", v: "violet" },
      { label: "Edge Computing", v: "violet" },
      { label: "Real-time Analytics" },
      { label: "Camera Networks" },
    ],
  },
  {
    client: "Drone Tech Software · Industrial & Defence",
    title: "Drone Fleet Management & Aerial Intelligence Platform",
    impact:
      "Autonomous mission planning, real-time telemetry, and AI-powered aerial inspection — from a single dashboard.",
    desc: "Industrial drone operations require more than remote controls. Ajinkya builds the software intelligence layer: fleet management dashboards for multi-drone deployments, AI-assisted mission planning, real-time telemetry and live video feeds, automated aerial inspection with anomaly detection, and post-flight data processing pipelines. The platform bridges drone hardware with enterprise data systems, turning aerial assets into actionable operational intelligence for industrial, infrastructure, and defence clients.",
    chips: [
      { label: "Drone Fleet Mgmt", v: "gold" },
      { label: "Telemetry", v: "gold" },
      { label: "Aerial AI", v: "violet" },
      { label: "Mission Planning" },
      { label: "Video Analytics" },
    ],
  },
  {
    client: "JSW Vijayanagar · IIoT Security Automation",
    title:
      "IIQ Tally Checker — Barcode-Driven Security & Logistics Intelligence",
    impact:
      "76,000+ monthly security entries digitised. Manual registries eliminated entirely. SAP synced in real time.",
    desc: "Security guards at JSW Vijayanagar were capturing all logistics data by hand in physical registries — creating delays, errors, and zero traceability. Ajinkya deployed IIQ Tally Checker: handheld barcode scanners feeding directly into SAP with 256-bit encrypted real-time reporting, user role management, and automated logs. What previously required teams of people and days of reconciliation now happens automatically, instantaneously, and with complete auditability.",
    chips: [
      { label: "Barcode / IIoT" },
      { label: "SAP Integration" },
      { label: "Android" },
      { label: "256-bit SHA4" },
    ],
  },
  {
    client: "JSW Steel · IIoT Maintenance Intelligence",
    title: "Smart VSOP System — QR-Enabled Equipment Maintenance",
    impact:
      "Instant, on-floor access to machine history, video SOPs, and spare parts — via a single QR scan.",
    desc: "Maintenance technicians at JSW Steel had no centralised system, no visual repair guides, and unreliable spare part tracking. Ajinkya built a QR-connected VSOP platform where every machine carries its entire intelligence on a single code: maintenance history, video SOPs, spare part inventory, real-time observations. Technicians scan and act. Admins manage through a cloud dashboard. Downtime shortened. Training accelerated. A simple QR code became the key to an entire IIoT maintenance intelligence layer.",
    chips: [
      { label: "QR Code / IoT" },
      { label: "Video SOPs" },
      { label: "Cloud" },
      { label: "Mobile App" },
    ],
  },
  {
    client: "Aditya Birla · Hindalco — AI-Powered EdTech",
    title: "Gamified Enterprise LMS — Scalable Learning at Hindalco",
    impact:
      "Powering learning for 71,200+ Hindalco employees globally — gamified, SCORM-ready, built on AutoScaling AI infrastructure.",
    desc: "Hindalco needed an experience, not just an LMS. Ajinkya built a gamified, web-based learning management system with SCORM eLearning modules, AI-tracked individual learner progress, ESSL hardware integration for attendance, and multi-role dashboards for HR, trainers, and leadership. AutoScaling database architecture ensures flawless performance whether 100 or 71,000 concurrent users are online — making it one of the most capable enterprise learning platforms built in India.",
    chips: [
      { label: "ReactJS" },
      { label: "PHP CI" },
      { label: "MongoDB" },
      { label: "SCORM" },
      { label: "ESSL Hardware" },
      { label: "Gamification AI", v: "violet" },
    ],
  },
  {
    client: "AKP Ferrocast · AI Smart Manufacturing",
    title: "AI-Driven Smart Manufacturing & Quality Control Platform",
    impact:
      "Unified procurement, production, and digital QC — all intelligent, all real-time, all traceable end-to-end.",
    desc: "AKP Ferrocast operated with fragmented systems, blind spots across procurement and production, and manual quality checks that couldn't scale. Ajinkya delivered a unified intelligent platform: RFID-based material traceability from raw inputs to finished goods, a digital quality control module with full audit trails, AI-informed live production dashboards, and secure role-based access. The transformation gave AKP Ferrocast the operational intelligence architecture of a global manufacturer.",
    chips: [
      { label: "RFID Traceability" },
      { label: "AI QC", v: "violet" },
      { label: "Real-time Dashboard" },
      { label: "Mobile App" },
    ],
  },
  {
    client: "JSW Steel · Machine Intelligence + IIoT",
    title: "DailyGate — Real-Time Machine Data & Predictive Maintenance",
    impact:
      "From reactive maintenance to predictive intelligence — machine data captured, analysed, and acted on automatically.",
    desc: "JSW Steel needed a smarter way to capture machine operational data, trigger proactive maintenance, and surface actionable insights to management. Ajinkya built DailyGate: a cross-platform IIoT solution with customisable data-capture forms, offline capability, in-form AI calculations, and automated maintenance alerts. Power BI-style dashboards surface trends and anomalies instantly. Clipboards, spreadsheets, and guesswork replaced by a real-time intelligence layer across the entire factory floor.",
    chips: [
      { label: "Android & iOS" },
      { label: "IIoT Sensors" },
      { label: "Offline Sync" },
      { label: "Predictive AI", v: "violet" },
      { label: "Power BI Dashboards" },
    ],
  },
];

const clients = [
  { type: "samsung" },
  { type: "jsw" },
  { type: "lg" },
  { name: "ADITYA BIRLA\nHINDALCO" },
  { name: "INDIAN\nARMY" },
  { name: "GOVT OF\nKARNATAKA" },
  { name: "IDFC FIRST\nBANK" },
  { name: "HDFC BANK" },
  { name: "BACARDÍ" },
  { name: "BIRLA SUN LIFE\nInsurance", small: "Insurance" },
  { name: "GRANT\nTHORNTON" },
  { name: "BMTC\nBangalore Metro", small: "Bangalore Metro" },
  { name: "DALMIA\nRefractories", small: "Refractories" },
  { name: "ADITYA BIRLA\nCAPITAL" },
  { name: "LOKMANYA\nMulti-State Co-op", small: "Multi-State Co-op" },
  { name: "MAITHAN\nCERAMIC" },
  { name: "FLUXGEN" },
  { name: "KOSHA" },
  { name: "MATRIX\nENGINEERING" },
  { name: "VIKAS\nCOMPOSITES" },
  { name: "ACHINT\nCHEMICALS" },
  { name: "AMBICA\nCLOTH STORES" },
  { name: "DATTAMSH\nLearning Simplified", small: "Learning Simplified" },
  { name: "3HUB\nTraining the Trainers", small: "Training the Trainers" },
  { name: "DELTASYS\nEFORMING" },
  { name: "BAJAJ\nASSOCIATES" },
  { name: "IFGL\nRefractories", small: "Refractories" },
  { name: "ALKEGEN" },
  { name: "BABAJI\nPHARMA" },
  { type: "more" },
];

const sectors = [
  "Manufacturing & Steel",
  "Healthcare & MedTech",
  "EdTech",
  "FinTech & Banking",
  "Defence & Army",
  "Cyber Security",
  "Drone Technology",
  "Industrial Automation",
  "IIoT & IoT",
  "Vision AI",
  "3D Printing",
  "Government",
  "Retail & FMCG",
  "Research Labs",
  "AR / VR",
  "Cloud Infrastructure",
  "SMEs & SMBs",
  "Corporate Enterprise",
];

const heroTags = [
  { label: "Artificial Intelligence" },
  { label: "Machine Learning" },
  { label: "Vision AI", cls: "gold" },
  { label: "Drone Tech Software", cls: "gold" },
  { label: "IIoT" },
  { label: "IoT" },
  { label: "RFID Intelligence" },
  { label: "Android & iOS" },
  { label: "Flutter" },
  { label: "React / Node" },
  { label: "SAP Integration" },
  { label: "Industrial Automation" },
  { label: "Cyber Security" },
  { label: "MedTech" },
  { label: "EdTech" },
];

const stats = [
  { num: "120+", label: "Team Members" },
  { num: "15+", label: "Industry Sectors" },
  { num: "90%", label: "Dispatch Error Reduction" },
  { num: "40%", label: "Faster Than Global Competitors" },
  { num: "₹80K Cr+", label: "Machinery Under Management" },
  { num: "27M T", label: "Steel Managed Annually" },
  { num: "7.2 L+", label: "Materials Tracked Daily" },
  { num: "76K+", label: "Monthly Entries Digitised at JSW" },
];

function TechChip({ label, v }) {
  return <span className={`ak-tech-chip${v ? ` ${v}` : ""}`}>{label}</span>;
}

function ClientCell({ client }) {
  if (client.type === "samsung")
    return (
      <div className="ak-client-cell">
        <div className="ak-logo-samsung">SAMSUNG</div>
      </div>
    );
  if (client.type === "jsw")
    return (
      <div className="ak-client-cell">
        <div className="ak-logo-jsw">JSW</div>
      </div>
    );
  if (client.type === "lg")
    return (
      <div className="ak-client-cell">
        <div className="ak-logo-lg">LG</div>
      </div>
    );
  if (client.type === "more")
    return (
      <div
        className="ak-client-cell"
        style={{ background: "rgba(0,212,255,0.03)" }}
      >
        <div
          className="ak-client-name"
          style={{ fontStyle: "italic", color: "#3a5a6a", fontSize: "8px" }}
        >
          + many more
        </div>
      </div>
    );
  const lines = client.name.split("\n");
  return (
    <div className="ak-client-cell">
      <div className="ak-client-name">
        {lines[0]}
        {lines[1] && (
          <>
            <br />
            <span style={{ fontSize: "7px" }}>{lines[1]}</span>
          </>
        )}
      </div>
    </div>
  );
}

export default function AmeyKadlePortfolio() {

  return (
    <div className="ak-root">
      <style>{styles}</style>
      <div className="ak-grid-bg" />
      <div className="ak-glow" />

      {/* HERO */}
      <div className="ak-hero">
        <div>
          <div className="ak-hero-eyebrow">
            <span className="ak-dot-live" />
            Founder &amp; CEO · Ajinkya Technologies · Belagavi &amp; Bangalore,
            India
          </div>
          <h1>
            Amey
            <br />
            <em>Kadle</em>
          </h1>
          <div className="ak-hero-role">
            // Engineering AI, IoT &amp; Vision Intelligence for India's Largest
            Enterprises
          </div>
          <p className="ak-hero-bio">
            A technology entrepreneur who built one of India's most quietly
            powerful enterprise software companies — delivering AI-powered
            platforms, IIoT ecosystems, Vision AI systems, and drone
            intelligence software for organisations like Samsung, JSW Steel,
            Aditya Birla Group, and the Indian Army. Ajinkya Technologies
            manages ₹80,000 Crore+ worth of industrial machinery, tracks 27
            million tons of steel annually, and operates across 15+ industries
            with a 120+ member team.
          </p>
          <div className="ak-hero-tags">
            {heroTags.map((t) => (
              <span
                key={t.label}
                className={`ak-tag${t.cls ? ` ${t.cls}` : ""}`}
              >
                {t.label}
              </span>
            ))}
          </div>
        </div>

        <div className="ak-stats-panel">
          {stats.map((s) => (
            <div key={s.label} className="ak-stat-cell">
              <span className="ak-stat-num">{s.num}</span>
              <span className="ak-stat-label">{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="ak-divider" />

      {/* SECTION 01 — TECHNOLOGY CAPABILITIES */}
      <section className="ak-section dark">
        <div className="ak-container">
          <div className="ak-section-header">
            <span className="ak-section-num">01</span>
            <h2 className="ak-section-title">Technology Capabilities</h2>
            <div className="ak-section-line" />
          </div>
          <p className="ak-section-sub">
            From IIoT sensor networks to Vision AI cameras and autonomous drone
            software — Ajinkya builds full-stack intelligence platforms that
            work in the real world.
          </p>
          <div className="ak-pillars-grid">
            {pillars.map((p) => (
              <div key={p.title} className="ak-pillar-card">
                <span className="ak-pillar-icon">{p.icon}</span>
                <div className="ak-pillar-title">{p.title}</div>
                <p className="ak-pillar-body">{p.body}</p>
                <div className="ak-pillar-chips">
                  {p.chips.map((c) => (
                    <TechChip key={c.label} {...c} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 02 — IMPACT METRICS */}
      <section className="ak-section">
        <div className="ak-container">
          <div className="ak-section-header">
            <span className="ak-section-num">02</span>
            <h2 className="ak-section-title">Value Created at Scale</h2>
            <div className="ak-section-line" />
          </div>
          <div className="ak-impact-grid">
            {impactMetrics.map((m) => (
              <div key={m.label} className="ak-impact-card">
                <span className="ak-impact-num">{m.num}</span>
                <span className="ak-impact-label">{m.label}</span>
                <p className="ak-impact-desc">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 03 — LANDMARK PARTNERSHIPS */}
      <section className="ak-section dark">
        <div className="ak-container">
          <div className="ak-section-header">
            <span className="ak-section-num">03</span>
            <h2 className="ak-section-title">Landmark Partnerships</h2>
            <div className="ak-section-line" />
          </div>

          <div className="ak-feature-card">
            <div className="ak-feature-badge">
              Strategic Alliance · AI + IoT · Global
            </div>
            <div className="ak-feature-title">
              Samsung Electronics × Ajinkya Technologies
            </div>
            <p className="ak-feature-body">
              Ajinkya Technologies and Samsung Electronics formalised a landmark
              strategic partnership to co-develop and deploy AI-powered,
              IoT-connected solutions across industrial, medical, and
              educational sectors. The collaboration brings Samsung's
              world-class R&amp;D capabilities and global technology ecosystem
              together with Ajinkya's deep domain engineering — translating
              cutting-edge research into deployable, measurable products at
              enterprise scale. This is a co-creation mandate: both
              organisations share innovation resources, technical
              infrastructure, and market access. For Ajinkya, it is the clearest
              signal yet of its standing as India's premier AI and IIoT
              integration partner for multinational technology corporations.
            </p>
            <div className="ak-feature-metrics">
              {[
                { n: "AI", l: "Industrial Intelligence" },
                { n: "IIoT", l: "Connected Systems" },
                { n: "MedTech", l: "Healthcare Solutions" },
                { n: "EdTech", l: "Learning Platforms" },
              ].map((m) => (
                <div key={m.l} className="ak-fm-cell">
                  <span className="ak-fm-num">{m.n}</span>
                  <div className="ak-fm-label">{m.l}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="ak-feature-card" style={{ marginTop: "1px" }}>
            <div className="ak-feature-badge">
              Government of Karnataka · Cybersecurity · Public Infrastructure
            </div>
            <div className="ak-feature-title">
              BrowseSafe — Citizen-Scale Security, Built by Ajinkya
            </div>
            <p className="ak-feature-body">
              Built in collaboration with ShreshtaIT under the Karnataka Cyber
              Security Department, BrowseSafe is an advanced Android security
              application available free of charge to every citizen of India. It
              demonstrates Ajinkya's rare capability to build consumer-scale,
              government-grade software — bridging enterprise-level security
              engineering with public digital infrastructure. More than a
              product, it is a statement: that sophisticated AI-backed security
              should be accessible to everyone, not just the enterprise.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 04 — SOLUTIONS */}
      <section className="ak-section">
        <div className="ak-container">
          <div className="ak-section-header">
            <span className="ak-section-num">04</span>
            <h2 className="ak-section-title">
              Solutions Built. Problems Solved.
            </h2>
            <div className="ak-section-line" />
          </div>
          <p className="ak-section-sub">
            Every solution below was engineered from scratch — not configured
            from templates. Real problems, measurable outcomes, built to last.
          </p>
          <div className="ak-solutions-grid">
            {solutions.map((s) => (
              <div key={s.title} className="ak-solution-card">
                <span className="ak-sol-client">{s.client}</span>
                <div className="ak-sol-title">{s.title}</div>
                <div className="ak-sol-impact">{s.impact}</div>
                <p className="ak-sol-desc">{s.desc}</p>
                <div className="ak-sol-tech">
                  {s.chips.map((c) => (
                    <TechChip key={c.label} {...c} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 05 — CLIENTS */}
      <section className="ak-section dark">
        <div className="ak-container">
          <div className="ak-section-header">
            <span className="ak-section-num">05</span>
            <h2 className="ak-section-title">Clients &amp; Partners</h2>
            <div className="ak-section-line" />
          </div>
          <div className="ak-clients-grid">
            {clients.map((c, i) => (
              <ClientCell key={i} client={c} />
            ))}
          </div>
          <div className="ak-clients-note">
            Spanning 15+ industries — Manufacturing · Defence · FinTech · EdTech
            · Healthcare · Government · Retail
          </div>
        </div>
      </section>

      {/* QUOTE */}
      <div className="ak-quote-wrap">
        <div className="ak-container">
          <div className="ak-quote-block">
            <p className="ak-quote-text">
              "Technology only earns its place when it makes a measurable
              difference — to the technician on the factory floor, the CFO
              reading the dashboard, and the citizen using the app. Everything
              else is noise."
            </p>
            <div className="ak-quote-attr">
              — Amey Kadle · Founder &amp; CEO, Ajinkya Technologies
            </div>
          </div>
        </div>
      </div>

      {/* SECTION 06 — SECTORS */}
      <section className="ak-section dark">
        <div className="ak-container">
          <div className="ak-section-header">
            <span className="ak-section-num">06</span>
            <h2 className="ak-section-title">Industries Served</h2>
            <div className="ak-section-line" />
          </div>
          <div className="ak-sectors-list">
            {sectors.map((s) => (
              <div key={s} className="ak-sector">
                {s}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="ak-footer">
        <div className="ak-container">
          <div className="ak-footer-inner">
            <div>
              <div className="ak-footer-name">
                Amey Kadle
                <span className="ak-cursor" />
              </div>
              <div className="ak-footer-meta">
                Founder &amp; CEO · Ajinkya Technologies · Belagavi &amp;
                Bangalore, Karnataka, India
              </div>
              <div className="ak-footer-meta" style={{ marginTop: ".3rem" }}>
                Smart. Scalable. Secure. That's Ajinkya.
              </div>
            </div>
            <div className="ak-footer-links">
              <a
                href="https://www.linkedin.com/in/ameykadle"
                target="_blank"
                rel="noreferrer"
              >
                LinkedIn — /ameykadle
              </a>
              <a
                href="https://ajinkyatechnologies.in"
                target="_blank"
                rel="noreferrer"
              >
                ajinkyatechnologies.in
              </a>
              <a href="mailto:amey@ajinkyatechnologies.in">
                amey@ajinkyatechnologies.in
              </a>
              <a href="tel:+917349416661">+91 73494 16661</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
